import { Link, useNavigate } from 'react-router-dom';
import { useLogout, useMe } from '../hooks/useAuth.js';
import { useMyListings } from '../hooks/useListings.js';
import { usePayoutBalance } from '../hooks/usePayouts.js';
import { useIdentityStatus } from '../hooks/useIdentity.js';

export function AccountPage() {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const listings = useMyListings();
  const payouts = usePayoutBalance();
  const identity = useIdentityStatus();

  const user = me.data?.user;
  const balance = payouts.data?.balanceCents ?? 0;
  const threshold = payouts.data?.thresholdCents ?? 10000;
  const idVerified = identity.data?.verified ?? false;

  return (
    <div className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="text-3xl font-bold text-navy mb-1">
        Hi{user?.firstName ? `, ${user.firstName}` : ''}
      </h1>
      <p className="text-navy-500 mb-10">{user?.email}</p>

      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <Tile
          to="/account/listings"
          title="My listings"
          subtitle={`${listings.data?.listings.length ?? 0} listed`}
          cta="Manage"
        />
        <Tile
          to="/account/payouts"
          title="Payouts"
          subtitle={
            balance >= threshold
              ? `$${(balance / 100).toFixed(2)} ready to claim`
              : balance > 0
                ? `$${(balance / 100).toFixed(2)} accrued`
                : 'No earnings yet'
          }
          cta="View"
          highlight={balance >= threshold}
        />
        <Tile
          to="/account/verify"
          title="ID verification"
          subtitle={idVerified ? 'Verified' : 'Required before first booking'}
          cta={idVerified ? 'View' : 'Verify'}
          highlight={!idVerified}
        />
      </div>

      {user?.referralCode && (
        <div className="card p-6 mb-6 border-orange/30 bg-orange/5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-navy mb-1">Refer a friend, earn $25</h2>
              <p className="text-sm text-navy-600">
                Share your code. When they complete their first rental, $25 credit lands on
                your next payout.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="font-mono font-bold text-navy bg-white border border-navy-100 rounded-lg px-3 py-2 text-sm">
                {user.referralCode}
              </code>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(user.referralCode ?? '');
                }}
                className="btn-ghost text-xs"
              >
                Copy
              </button>
            </div>
          </div>
          {typeof user.referralBonusCents === 'number' && user.referralBonusCents > 0 && (
            <p className="mt-3 text-xs font-semibold text-orange">
              ${(user.referralBonusCents / 100).toFixed(2)} in referral credit will be added
              to your next payout.
            </p>
          )}
        </div>
      )}

      <div className="card p-6 bg-gradient-to-br from-navy to-navy-700 text-white">
        <h2 className="text-xl font-bold mb-2">Own a trailer that sits idle?</h2>
        <p className="text-navy-100 mb-4">
          Earn $200–$600 a month renting it on TowShare. We handle payments and insurance.
        </p>
        <Link to="/list-your-trailer" className="btn-primary inline-flex">
          List your trailer
        </Link>
      </div>

      <div className="mt-10 lg:hidden">
        <button
          className="btn-ghost w-full border border-navy-100 text-red-600"
          onClick={async () => {
            await logout.mutateAsync();
            navigate('/');
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

function Tile({
  to,
  title,
  subtitle,
  cta,
  highlight,
}: {
  to: string;
  title: string;
  subtitle: string;
  cta: string;
  highlight?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`card p-6 hover:shadow-md transition-all flex justify-between items-center ${
        highlight ? 'border-orange-200 bg-orange-50/40' : ''
      }`}
    >
      <div>
        <div className="font-semibold text-navy">{title}</div>
        <div className="text-sm text-navy-500 mt-0.5">{subtitle}</div>
      </div>
      <span className="text-orange font-medium text-sm">{cta} →</span>
    </Link>
  );
}
